import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { UserContext } from './UserContext';
import { fetchChildDashboard } from '../api';

export const ChildContext = createContext();

export const ChildProvider = ({ children }) => {
    const { user } = useContext(UserContext);
    const [childData, setChildData] = useState({
        dashboardData: null,
        loading: true,
        error: '',
        daysUntilNextAllowance: 0,
        distributionDay: '',
        currencyCode: '' // Added currencyCode to initial state
    });

    const calculateNextAllowanceDays = useCallback((distributionDay, timezone) => {
        const dayOfWeekMap = {
            'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
            'Thursday': 4, 'Friday': 5, 'Saturday': 6
        };

        const today = new Date();
        const todayDayOfWeek = today.getUTCDay();
        const distributionDayOfWeek = dayOfWeekMap[distributionDay];

        let daysToAdd = distributionDayOfWeek - todayDayOfWeek;
        if (daysToAdd <= 0) {
            daysToAdd += 7;
        }

        const nextAllowanceDate = new Date(new Date().setDate(today.getDate() + daysToAdd));
        nextAllowanceDate.setHours(0, 0, 0, 0);

        const diffTime = Math.abs(nextAllowanceDate - today);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        return diffDays;
    }, []);

    const fetchChildData = useCallback(async () => {
        if (user.details?.user_id) {
            setChildData(prev => ({ ...prev, loading: true }));
            try {
                const data = await fetchChildDashboard(user.details.user_id);
                console.log ("child.context data: ", data);
                const daysUntilNextAllowance = calculateNextAllowanceDays(data.dayOfDistribution, user.details.timezone);
                setChildData({
                    dashboardData: data,
                    loading: false,
                    error: '',
                    daysUntilNextAllowance,
                    distributionDay: data.dayOfDistribution,
                    currencyCode: data.currencyCode // Added currencyCode to setChildData
                });
            } catch (err) {
                console.error('Error fetching dashboard data:', err);
                setChildData(prev => ({
                    ...prev,
                    error: 'Failed to load dashboard data.',
                    loading: false
                }));
            }
        }
    }, [user.details?.user_id, user.details?.timezone, calculateNextAllowanceDays]);

    useEffect(() => {
        fetchChildData();
    }, [fetchChildData]);

    return (
        <ChildContext.Provider value={{
            ...childData,
            refetchChildData: fetchChildData
        }}>
            {children}
        </ChildContext.Provider>
    );
};

export const useChild = () => {
    const context = useContext(ChildContext);
    if (context === undefined) {
        throw new Error('useChild must be used within a ChildProvider');
    }
    return context;
};